import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Assessment, Close, GetApp, Search } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import { baixarCsv, csvLinha } from "../../../functions/exportarCsv"
import type { FechamentoItem } from "../../../services/historicoFechamentos"
import { obterDetalhamentoFechamento } from "../../../services/historicoFechamentos"
import { useHistoricoFechamentosContext } from "../context/useHistoricoFechamentosContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    detalhamentoHtml: {
        fontFamily: "monospace",
        fontSize: "0.85rem",
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
    },
}))

function formatarData(millis: number): string {
    return new Date(millis).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    })
}

function formatarMoeda(valor: number | null | undefined): string {
    if (valor == null) return "R$ 0,00"
    return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

// Converte o detalhamento (HTML/texto do "Resumo do Movimento") em texto plano, preservando
// as quebras de linha, para caber em uma célula do CSV.
function htmlParaTexto(html: string): string {
    if (!html) return ""
    const comQuebras = html
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|tr|li|h[1-6])>/gi, "\n")
    const el = document.createElement("div")
    el.innerHTML = comQuebras
    const texto = el.textContent || el.innerText || ""
    return texto
        .replace(/\n{3,}/g, "\n\n")
        .replace(/[ \t]+\n/g, "\n")
        .trim()
}

type DialogDetalhamentoProps = {
    fechamento: FechamentoItem | null
    onClose: () => void
}

function DialogDetalhamento({ fechamento, onClose }: DialogDetalhamentoProps) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const [detalhamento, setDetalhamento] = useState<string | null>(null)
    const [carregando, setCarregando] = useState(false)

    useEffect(() => {
        if (!fechamento) return
        setDetalhamento(null)
        setCarregando(true)
        const autorizacao = localStorage.getItem("chaveSistema") || ""
        obterDetalhamentoFechamento(autorizacao, fechamento.dataInicio)
            .then(res => {
                if ("retornoErro" in res) {
                    enqueueSnackbar(res.mensagem || "Erro ao carregar detalhamento", {
                        variant: "error",
                    })
                } else {
                    setDetalhamento(res.detalhamento)
                }
            })
            .finally(() => setCarregando(false))
    }, [fechamento])

    if (!fechamento) return null

    return (
        <Dialog open={!!fechamento} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Detalhamento — {formatarData(fechamento.dataInicio)}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {carregando && (
                    <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                        Carregando...
                    </Typography>
                )}
                {!carregando && detalhamento && (
                    <Typography
                        component="div"
                        className={classes.detalhamentoHtml}
                        dangerouslySetInnerHTML={{ __html: detalhamento }}
                    />
                )}
                {!carregando && !detalhamento && (
                    <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                        Nenhum detalhamento disponível.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    )
}

export function TabelaHistoricoFechamentos() {
    const classes = useStyles()
    const { dados, erroCarregamento, dataInicio, dataFim, setDataInicio, setDataFim, recarregar } =
        useHistoricoFechamentosContext()
    const { enqueueSnackbar } = useSnackbar()
    const [fechamentoSelecionado, setFechamentoSelecionado] = useState<FechamentoItem | null>(null)
    const [exportando, setExportando] = useState(false)

    useEffect(() => {
        if (erroCarregamento) {
            enqueueSnackbar(erroCarregamento, { variant: "error" })
        }
    }, [erroCarregamento])

    const handleBuscar = () => {
        if (dataInicio && dataFim && dataInicio > dataFim) {
            enqueueSnackbar("A data inicial não pode ser maior que a final.", { variant: "warning" })
            return
        }
        recarregar(dataInicio || undefined, dataFim || undefined)
    }

    // Exporta o período em CSV, incluindo o detalhamento (Resumo do Movimento) de cada dia,
    // buscado individualmente no servidor.
    const handleExportar = async () => {
        if (dados.fechamentos.length === 0) {
            enqueueSnackbar("Não há fechamentos no período para exportar.", { variant: "info" })
            return
        }
        try {
            setExportando(true)
            const autorizacao = localStorage.getItem("chaveSistema") || ""
            const linhas: string[] = [csvLinha(["Data", "Total Vendido", "Detalhamento"])]
            for (const f of dados.fechamentos) {
                let detalhe = ""
                const res = await obterDetalhamentoFechamento(autorizacao, f.dataInicio)
                if (!("retornoErro" in res)) {
                    detalhe = htmlParaTexto(res.detalhamento)
                }
                linhas.push(
                    csvLinha([formatarData(f.dataInicio), formatarMoeda(f.valorTotalVendido), detalhe])
                )
            }
            baixarCsv(`historico-fechamentos_${dataInicio}_a_${dataFim}.csv`, linhas)
        } catch {
            enqueueSnackbar("Erro ao exportar os fechamentos.", { variant: "error" })
        } finally {
            setExportando(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={3}>
                    <Typography variant="h5" className={classes.titleText}>
                        Histórico de Fechamentos
                    </Typography>
                    <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                        Aqui você consegue visualizar o histórico de fechamentos da loja.
                    </Typography>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Assessment className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Fechamentos
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filterRow}>
                            <TextField
                                label="Início"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <TextField
                                label="Fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Search />}
                                onClick={handleBuscar}
                                style={{ borderRadius: 25 }}
                            >
                                Buscar
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportar}
                                disabled={exportando || dados.fechamentos.length === 0}
                                style={{ borderRadius: 25 }}
                            >
                                {exportando ? "Exportando..." : "Exportar"}
                            </Button>
                        </Box>

                        {dados.fechamentos.length === 0 ? (
                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                Seu estabelecimento não possui fechamentos.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ width: "100%", minWidth: 350 }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 120 }}>Data</TableCell>
                                            <TableCell style={{ minWidth: 110 }}>
                                                Total Vendido
                                            </TableCell>
                                            <TableCell style={{ minWidth: 120 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.fechamentos.map((f: FechamentoItem) => (
                                            <TableRow key={f.dataInicio} hover>
                                                <TableCell>{formatarData(f.dataInicio)}</TableCell>
                                                <TableCell>
                                                    {formatarMoeda(f.valorTotalVendido)}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => setFechamentoSelecionado(f)}
                                                        style={{
                                                            borderRadius: 20,
                                                            fontSize: "0.7rem",
                                                        }}
                                                    >
                                                        Ver Detalhes
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>

            <DialogDetalhamento
                fechamento={fechamentoSelecionado}
                onClose={() => setFechamentoSelecionado(null)}
            />
        </Box>
    )
}
